import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { store as accBootStore } from "../../Lit/acc-boot/acc-boot.js";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useMaxUiKpis } from "../common/hooks/useMaxUiKpis";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import LeafTabViews, { TabView } from "../../../common/leaf/LeafTabViews/LeafTabViews";
import { setActiveTabView } from "../../../reducers/renewalsArReport";
import defaultContent from "../../../content/actionReportsRenewals";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { FetchRenewalsActionReportData } from "./RenewalsActionReportDataLayer";
import { RenewalABO } from "../../../services/ActionReports/ActionReportAPI.types";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ActionReportsTreeView } from "../common/ActionReportTreeView/ActionReportsTreeView";
import { getBusinessNatureTag, isCustomerBusinessNature } from "../utils/businessNature";
import { updatePeriod } from "../../../reducers/period";
import { getCurrentPeriod } from "../../../common/util/period";
import {
  MODULE_RENEWALS,
  MODULE_RENEWALS_EARLY_RENEWAL_PERIOD,
  MODULE_RENEWALS_IN_GRACE_PERIOD,
  MODULE_RENEWALS_IN_RENEWAL_PERIOD,
  MODULE_RENEWALS_NOT_ON_AUTO_RENEWAL,
  MODULE_RENEWALS_ON_AUTO_RENEWAL,
} from "../../../common/constants/export-constants";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { buildFieldTitleOverrides } from "../../../common/util/exportUtil";
import "../ActionReports.scss";
import "./RenewalsActionReport.scss";
import { setAffAbosToExpand } from "../../../reducers/renewalsArReport";
import type { RecordRef } from "../../../services/ActionReports/ActionReportAPI.types";
import actionReportsContent from "../../../content/actionReports";
import { selectGroupLeader, setSelectedLeader } from "../../../reducers/groupLeaderFilter";
import { useGroupLeaderUrlSync } from "../../../common/hooks/useGroupLeaderUrlSync";
import { GroupLeaderFilter } from "../common/GroupLeaderFilter/GroupLeaderFilter";
import { GroupLeaderFilterErrorBoundary } from "../common/GroupLeaderFilter/GroupLeaderFilterErrorBoundary";
import { useGetSavedKpiPreferenceQuery } from "../../../services/KPIPreferences/savedKpiPreferencesAPI";
import { GroupLeaderSection } from "../common/GroupLeaderSection/GroupLeaderSection";
import {
  buildLeaderSections,
  leaderExistsInTree,
  parentLeaderOf,
  recordsInLeaderSubtree,
  type LeaderSection,
} from "../common/groupLeaderGrouping";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import {
  renewalsPageViewTrack,
  renewalsTabClickTrack,
  renewalsExportTrack,
  renewalsProfileTrack,
  renewalsCustomizeKpiTrack,
  renewalsApplyCustomizeKpiTrack,
  renewalsAddToWatchlistTrack,
  renewalsPhoneIconTrack,
  renewalsTreeViewTrack,
  renewalsListViewTrack,
  renewalsMessageIconTrack,
  renewalsCancelCustomizeKpiTrack,
  renewalsSearchTrack,
  renewalsSelectGroupLeaderTrack,
} from "../../../common/tealium/ActionReports/RenewalsTrack";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";
import { ErrorTile } from "../../../common/components/ErrorTile/ErrorTile";
import maskAboNumber from "../../../common/util/maskAboNumber";
import { IconSquareCheck } from "../../../common/icons/IconSquareCheck/IconSquareCheck";
import { IconError } from "../../../common/icons/IconError/IconError";
import type { FieldConfig } from "../common/ActionReportListView/types";

export enum renewalsReportTabs {
  IN_RENEWAL_PERIOD = "in_renewal_period",
  IN_GRACE_PERIOD = "in_grace_period",
  ON_AUTO_RENEWAL = "on_auto_renewal",
  NON_AUTO_RENEWAL = "not_on_auto_renewal",
  EARLY_RENEWAL_PERIOD = "early_renewal_period",
}

export const isTreeViewEnabledForTab = (tab: renewalsReportTabs): boolean =>
  tab === renewalsReportTabs.IN_RENEWAL_PERIOD;

// Driven by the market config (available at boot) rather than the persisted activeTabs, which still
// hold the previous visit's tabs until the renewals response arrives.
export const getPageDescriptionKey = (
  enableEarlyRenewalPeriod?: boolean,
): "pageDescription" | "pageDescriptionEarlyRenewal" =>
  enableEarlyRenewalPeriod ? "pageDescriptionEarlyRenewal" : "pageDescription";

const DEFAULT_KPIS = [
  "renewalDate",
  "ppv",
  "annualPPV",
  "groupPV",
  "frontlines",
  "totalABOCount",
  "sponsoring",
  "sponsoringYtd",
];

// Match the padding rule used by the GL grouping helpers — record `aff`s
// arrive unpadded ("70") while leader/ancestor refs from compute are zero-
// padded to 3 digits ("070"). Numeric inputs get padded; non-numeric inputs
// pass through.
const padAffSafe = (aff: string): string => {
  if (aff.length >= 3) return aff;
  return /^\d+$/.test(aff) ? aff.padStart(3, "0") : aff;
};

export const renderRenewalConsent = (renewalConsent: unknown) => {
  if (typeof renewalConsent !== "boolean") return "--";
  return renewalConsent ? <IconSquareCheck /> : <IconError />;
};

const createFieldConfig = (tableColumns: { [key: string]: string }): FieldConfig[] => [
  { id: KPI_ID.RENEWAL_DATE, kpi: "renewalDate", title: tableColumns["renewalDate"] },
  { id: KPI_ID.PERSONAL_PV, kpi: "ppv", title: tableColumns["ppv"] },
  { id: KPI_ID.ANNUAL_PPV, kpi: KPI_ID.ANNUAL_PPV, title: tableColumns["annualPPV"] },
  { id: KPI_ID.GROUP_PV, kpi: "groupPV", title: tableColumns["gpv"] },
  { id: KPI_ID.FRONTLINES, kpi: "frontlines", title: tableColumns["frontlines"] },
  { id: KPI_ID.GROUP_SIZE, kpi: "totalABOCount", title: tableColumns["groupSize"] },
  { id: KPI_ID.SPONSORING, kpi: "sponsoring", title: tableColumns["sponsoring"] },
  { id: KPI_ID.SPONSORING_YTD, kpi: "sponsoringYtd", title: tableColumns["sponsoringYtd"] },
  {
    id: KPI_ID.RENEWAL_CONSENT,
    kpi: "renewalConsent",
    title: tableColumns["renewalConsent"],
    excludeFromEnrichment: true,
    renderValue: (_value, row) => renderRenewalConsent(row.renewalConsent),
  },
];

export const getConfiguredSelectedKpis = (
  savedKpis: { id: string; displaydefault: boolean }[] | undefined,
  availableKpis: string[] | null | undefined,
): string[] => {
  // Configuration is authoritative: an unavailable KPI must not be rendered or exported,
  // even when an older saved MyBiz preference still marks it as selected.
  if (!Array.isArray(availableKpis)) {
    return [];
  }

  if (!savedKpis || savedKpis.length === 0) {
    return DEFAULT_KPIS.filter((kpi) => availableKpis.includes(kpi));
  }

  return savedKpis.filter((kpi) => kpi.displaydefault && availableKpis.includes(kpi.id)).map((kpi) => kpi.id);
};

const useSelectedKpis = () => {
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  const { configuration } = useAppSelector((state) => state.boot);
  const availableKpis = configuration.uiKpis.renewalsV2;
  useGetSavedKpiPreferenceQuery("renewalsKPIs", { refetchOnMountOrArgChange: true });

  return useMemo(
    () =>
      getConfiguredSelectedKpis(savedKpiPreferencesData?.savedDataViews?.renewalsKPIs?.availableKpis, availableKpis),
    [savedKpiPreferencesData, availableKpis],
  );
};

const useTreeView = (getActiveTabData: () => RenewalABO[], activeView: renewalsReportTabs, isLoading: boolean) => {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isTreeView, setIsTreeView] = useState(hash.includes("tree") || false);

  useLayoutEffect(() => {
    if (isLoading) return;
    const tabData = getActiveTabData();
    if ((!isTreeViewEnabledForTab(activeView) || tabData?.length === 0) && isTreeView) {
      setIsTreeView(false);
      accBootStore.setState({ treeView: { affAbosToExpand: [] } });
      navigate(`#${activeView}`);
    }
  }, [activeView, navigate, isTreeView, getActiveTabData, isLoading]);

  const handleToggle = useCallback(() => {
    if (isTreeView || hash.includes("tree")) {
      renewalsListViewTrack();
      setIsTreeView(false);
      accBootStore.setState({ treeView: { affAbosToExpand: [] } });
      navigate(`#${activeView}`);
    } else {
      renewalsTreeViewTrack();
      const tabData = getActiveTabData();
      const affAbosToExpand = tabData.map((i) => `${i.aff.padStart(3, "0")}-${i.abo}`);
      dispatch(setAffAbosToExpand(affAbosToExpand));
      const affAbos = tabData.map((i) => `${i.aff.padStart(3, "0")}-${i.abo}`);
      accBootStore.setState({
        treeView: { affAbosToExpand: affAbos },
      });
      setIsTreeView(true);
      navigate("#tree");
    }
  }, [isTreeView, hash, navigate, getActiveTabData, dispatch, activeView]);

  return { isTreeView, handleToggle };
};

type RenewalSearchEntry = {
  abo: string;
  aff: string;
  isoCountry: string;
  aboName: string;
  tab: renewalsReportTabs;
  indexInTab: number;
  firstGroupLeader?: RecordRef;
  isConfidential: boolean;
};

export default function RenewalsActionReport() {
  const { t } = useTranslation(["actionReportsRenewals", "error", "ssiUplineReport", "actionReports"]);
  const { activeView, activeTabs } = useAppSelector((state) => state.renewalActionReportTracker);
  const confidentialUser = t("ConfidentialUser", "Confidential User", { ns: "leafAboName" });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { configuration } = useAppSelector(({ boot }) => boot);
  const { uiKpis } = configuration;
  const enableGroupLeaderFilter = configuration.actionReports.enableGroupLeaderFilter;
  const showRenewalsIncludesCustomers = configuration.actionReports.showRenewalsIncludesCustomers;
  const [enableTreeView, setEnableTreeView] = useState(false);
  const isMaskAboNumber = useAppSelector((s) => s.boot.configuration.actionReports.maskConfidentialAboNumber) ?? false;
  const maxUiKpis = useMaxUiKpis();
  const currentPeriod = getCurrentPeriod();

  useEffect(() => {
    dispatch(updatePeriod({ period: currentPeriod }));
  }, [dispatch, currentPeriod]);

  const [pageViewFired, setPageViewFired] = useState(false);

  const { renewalsActionReportData, isLoading, errorStatus, refetch } = FetchRenewalsActionReportData();
  const selectedLeader = useAppSelector(selectGroupLeader);
  const groupLeaders = useMemo(
    () => renewalsActionReportData.groupLeaders ?? [],
    [renewalsActionReportData.groupLeaders],
  );
  const groupLeaderTree = useMemo(
    () => renewalsActionReportData.groupLeaderTree ?? [],
    [renewalsActionReportData.groupLeaderTree],
  );
  // Pass the raw (possibly undefined) field so the hook defers URL hydration
  // until the leader list has actually loaded — otherwise a deep-link `?gl=`
  // can be cleared against the pre-fetch empty default.
  useGroupLeaderUrlSync(isLoading ? undefined : renewalsActionReportData.groupLeaders);

  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>("");
  // The ABO the user searched for. Persists across renders so that the
  // scrollToIndex passed to the list view is always computed against the
  // list that's actually being rendered — without it, a search that also
  // changes the leader filter races: the index is computed before the
  // filter is applied, so the first search lands on the wrong row.
  const [scrollToTarget, setScrollToTarget] = useState<RecordRef | null>(null);

  const getActiveTabData = useCallback(() => {
    switch (activeView) {
      case renewalsReportTabs.IN_RENEWAL_PERIOD:
        return renewalsActionReportData.inRenewal;
      case renewalsReportTabs.IN_GRACE_PERIOD:
        return renewalsActionReportData.gracePeriod;
      case renewalsReportTabs.EARLY_RENEWAL_PERIOD:
        return renewalsActionReportData.earlyRenewal;
      case renewalsReportTabs.ON_AUTO_RENEWAL:
        return renewalsActionReportData.autoRenewal;
      case renewalsReportTabs.NON_AUTO_RENEWAL:
        return renewalsActionReportData.nonAutoRenewal;
      default:
        return [];
    }
  }, [activeView, renewalsActionReportData]);

  const getFirstGroupLeader = (r: RenewalABO): RecordRef | undefined => r.firstGroupLeader;
  const getSelfRef = (r: RenewalABO): RecordRef => ({ aff: r.aff, abo: r.abo });

  const searchPool = useMemo<RenewalSearchEntry[]>(() => {
    const tabDataSources = [
      { tab: renewalsReportTabs.IN_RENEWAL_PERIOD, data: renewalsActionReportData.inRenewal },
      { tab: renewalsReportTabs.IN_GRACE_PERIOD, data: renewalsActionReportData.gracePeriod },
      { tab: renewalsReportTabs.EARLY_RENEWAL_PERIOD, data: renewalsActionReportData.earlyRenewal },
      { tab: renewalsReportTabs.ON_AUTO_RENEWAL, data: renewalsActionReportData.autoRenewal },
      { tab: renewalsReportTabs.NON_AUTO_RENEWAL, data: renewalsActionReportData.nonAutoRenewal },
    ];
    const allItems = tabDataSources.flatMap(({ data }) => data);
    // Always search the full record set so ABOs from any leader's subtree are
    // findable. Selection auto-switches the leader filter to the found ABO's leader.
    const scopedKeys = new Set(allItems.map((r) => `${r.aff}|${r.abo}`));

    const seen = new Set<string>();
    return tabDataSources.flatMap(({ tab, data }) =>
      data.reduce<RenewalSearchEntry[]>((acc, item, indexInTab) => {
        const key = `${item.aff}-${item.abo}`;
        if (!seen.has(key) && scopedKeys.has(`${item.aff}|${item.abo}`)) {
          seen.add(key);
          acc.push({
            abo: item.abo,
            aff: item.aff,
            isoCountry: item.country,
            aboName: item.aboName === "--" ? confidentialUser : item.aboName,
            tab,
            indexInTab,
            firstGroupLeader: item.firstGroupLeader,
            isConfidential: item.isConfidential,
          });
        }
        return acc;
      }, []),
    );
  }, [renewalsActionReportData, confidentialUser]);

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) => {
      return searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map((entry): SearchSelectItem => {
          const sublabel = `${isMaskAboNumber && entry.isConfidential ? maskAboNumber(entry.abo) : entry.abo} | ${getCountryNameFromCode(entry.isoCountry, storedLocale())}`;
          return {
            key: `${entry.aff}-${entry.abo}`,
            label: entry.aboName,
            sublabel,
          };
        });
    },
    [searchPool, isMaskAboNumber],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}` === key);
      if (found) {
        const treeViewActive = hash.includes("tree");

        if (treeViewActive) {
          document.dispatchEvent(
            new CustomEvent("acc-los-data-find", { detail: { record: { aboNumber: found.abo } } }),
          );
          setSearchText(found.aboName);
          return { displayName: found.aboName };
        }

        dispatch(setActiveTabView(found.tab));

        // The dropdown selection that makes the searched row visible is
        // always the leader the searched ABO sits *under*:
        //   - If the ABO is a group leader, that's their parent leader
        //     in the tree (or "All" if they're top-level).
        //   - Otherwise, that's their firstGroupLeader from the record.
        // The searched row never lives in its own section — leader rows
        // are bucketed under their parent's section by buildLeaderSections
        // and recordsInLeaderSubtree.
        // The dropdown selection should always be the leader the searched
        // ABO sits *under*. Two ways an ABO can resolve to "is a leader":
        //   1. They appear as a node in the GL tree.
        //   2. Their record's firstGroupLeader points at themselves
        //      (legacy backend self-reference).
        // Either way, scoping must use the *parent* leader, not the leader
        // themselves — otherwise the filter chip shows the leader's name
        // and the user has to search a second time to actually navigate.
        const selfRef: RecordRef = { aff: found.aff, abo: found.abo };
        const isInTree = leaderExistsInTree(groupLeaderTree, selfRef);
        const fglMatchesSelf =
          !!found.firstGroupLeader &&
          padAffSafe(found.firstGroupLeader.aff) === padAffSafe(found.aff) &&
          found.firstGroupLeader.abo === found.abo;
        const isSelfLeader = isInTree || fglMatchesSelf;

        let targetLeader: RecordRef | null = null;
        if (groupLeaders.length > 0) {
          if (isSelfLeader) {
            targetLeader = parentLeaderOf(groupLeaderTree, selfRef);
          } else if (found.firstGroupLeader && (found.firstGroupLeader.aff || found.firstGroupLeader.abo)) {
            targetLeader = found.firstGroupLeader;
          }
        }

        if (groupLeaders.length > 0) {
          dispatch(setSelectedLeader(targetLeader));
        }

        setScrollToIndex(undefined);
        setSearchText(found.aboName);
        // Set the scroll target. The render path resolves it to an index
        // against whichever list actually contains the row, after the
        // leader-filter dispatch above has applied. The virtualized table
        // retries the imperative scroll across a few frames so freshly
        // mounted instances still receive the scroll on the first try.
        setScrollToTarget({ aff: found.aff, abo: found.abo });
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool, hash, dispatch, groupLeaders.length, groupLeaderTree],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToIndex(undefined);
    setScrollToTarget(null);
  }, []);

  useEffect(() => {
    if (isLoading || !activeTabs?.length || hash === "#tree") return;

    const tabFromHash = hash.replace("#", "") as renewalsReportTabs;
    const hashHasValidTab = Object.values(renewalsReportTabs).includes(tabFromHash) && activeTabs.includes(tabFromHash);

    if (hashHasValidTab) {
      dispatch(setActiveTabView(tabFromHash));
    } else {
      const defaultTab = activeTabs.includes(renewalsReportTabs.IN_RENEWAL_PERIOD)
        ? renewalsReportTabs.IN_RENEWAL_PERIOD
        : renewalsReportTabs.NON_AUTO_RENEWAL;
      dispatch(setActiveTabView(defaultTab));
      navigate(`#${defaultTab}`, { replace: true });
    }
  }, [activeTabs, isLoading, dispatch, navigate, hash]);

  useEffect(() => {
    if (!isLoading && !pageViewFired && activeTabs?.length) {
      renewalsPageViewTrack(currentPeriod, activeView);
      setPageViewFired(true);
    }
  }, [isLoading, activeView, currentPeriod, pageViewFired, activeTabs]);

  const { isTreeView, handleToggle } = useTreeView(getActiveTabData, activeView, isLoading);

  useEffect(() => {
    if (isTreeView) {
      const tabData = getActiveTabData();
      const affAbos = tabData.map((i) => `${i.aff.padStart(3, "0")}-${i.abo}`);
      dispatch(setAffAbosToExpand(affAbos));
      accBootStore.setState({
        treeView: { affAbosToExpand: affAbos },
      });
    }
  }, [activeView, isTreeView, getActiveTabData, dispatch]);

  const tableColumnContent: { [key: string]: string } = {
    ...defaultContent.table.columns,
    ...(t("table.columns", {
      ns: "actionReportsRenewals",
      returnObjects: true,
      defaultValue: defaultContent.table.columns,
    }) as { [key: string]: string }),
  };

  const selectedKpis = useSelectedKpis();
  const fieldConfig = useMemo(() => createFieldConfig(tableColumnContent), [tableColumnContent]);

  const setViewOnClick = (activeView: string) => {
    const nextTab = activeView as renewalsReportTabs;

    renewalsTabClickTrack(nextTab);
    dispatch(setActiveTabView(nextTab));
    navigate(`#${activeView}`);
    setScrollToIndex(undefined);
    setScrollToTarget(null);
  };

  const arWrapperList = (affAboList: RenewalABO[]) => {
    if (affAboList.length === 0) {
      return (
        <div className="Renewals__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    const reportData = affAboList.map((record) => ({
      aboName: record.aboName == "--" ? confidentialUser : record.aboName,
      aboNumber: record.abo,
      abo: record.abo,
      aff: record.aff,
      isInternational: record.isInternational,
      countryCode: record.country,
      phoneNumber: record.phoneNumber,
      whatsapp: record.whatsapp,
      level: record.level,
      imageRefUrl: record.imageRefUrl,
      isCustomer: isCustomerBusinessNature(record.businessNature),
      businessNature: record.businessNature,
      businessNatureTag: showRenewalsIncludesCustomers ? getBusinessNatureTag(record.businessNature) : undefined,
      isConfidential: record.isConfidential,
      renewalConsent: record.renewalConsent,
    }));

    // Resolve the persisted scroll target against this specific list.
    // Multiple lists render in the All view (one per leader section); only
    // the list that actually contains the searched ABO gets a defined
    // scrollToIndex. Falls back to the explicit `scrollToIndex` state for
    // legacy code paths that still set it directly.
    const resolvedIndex = scrollToTarget
      ? (() => {
          const i = affAboList.findIndex((r) => r.aff === scrollToTarget.aff && r.abo === scrollToTarget.abo);
          return i >= 0 ? i : undefined;
        })()
      : scrollToIndex;

    return (
      <div className="Renewals__tableWrapper">
        <ActionReportListView
          selectedKpis={selectedKpis}
          reportId="renewalsAR"
          reportData={reportData}
          fieldConfig={fieldConfig}
          scrollToIndex={resolvedIndex}
          enableWatchlist={true}
          showImageBlock={true}
          showTagForDeepCustomers={showRenewalsIncludesCustomers === true}
          requestWithSponsorForCustomers={showRenewalsIncludesCustomers === true}
          onProfileTrack={renewalsProfileTrack}
          onWatchlistTrack={(isAdding) => {
            if (isAdding) renewalsAddToWatchlistTrack();
          }}
          onPhoneTrack={renewalsPhoneIconTrack}
          onMessageTrack={renewalsMessageIconTrack}
        />
      </div>
    );
  };

  const exportType = useMemo(() => {
    switch (activeView) {
      case renewalsReportTabs.IN_RENEWAL_PERIOD:
        return MODULE_RENEWALS_IN_RENEWAL_PERIOD;
      case renewalsReportTabs.IN_GRACE_PERIOD:
        return MODULE_RENEWALS_IN_GRACE_PERIOD;
      case renewalsReportTabs.ON_AUTO_RENEWAL:
        return MODULE_RENEWALS_ON_AUTO_RENEWAL;
      case renewalsReportTabs.NON_AUTO_RENEWAL:
        return MODULE_RENEWALS_NOT_ON_AUTO_RENEWAL;
      case renewalsReportTabs.EARLY_RENEWAL_PERIOD:
        return MODULE_RENEWALS_EARLY_RENEWAL_PERIOD;
      default:
        return "";
    }
  }, [activeView]);

  const startedByLabels = useMemo<Record<string, string>>(
    () => ({
      [MODULE_RENEWALS_IN_RENEWAL_PERIOD]: t("tabs.inRenewalPeriod", defaultContent.tabs.inRenewalPeriod).toString(),
      [MODULE_RENEWALS_IN_GRACE_PERIOD]: t("tabs.inGracePeriod", defaultContent.tabs.inGracePeriod).toString(),
      [MODULE_RENEWALS_EARLY_RENEWAL_PERIOD]: t(
        "tabs.earlyRenewalPeriod",
        defaultContent.tabs.earlyRenewalPeriod,
      ).toString(),
      [MODULE_RENEWALS_ON_AUTO_RENEWAL]: t("tabs.onAuto", defaultContent.tabs.onAuto).toString(),
      [MODULE_RENEWALS_NOT_ON_AUTO_RENEWAL]: t("tabs.notOnAuto", defaultContent.tabs.notOnAuto).toString(),
    }),
    [t],
  );

  const customerAboNumberTitle = t("customerAboNumber", defaultContent.customerAboNumber as string);

  const fieldTitleOverrides = useMemo(() => {
    const overrides = buildFieldTitleOverrides(tableColumnContent);
    if (showRenewalsIncludesCustomers) {
      overrides["aboNo"] = customerAboNumberTitle;
    }
    return overrides;
  }, [tableColumnContent, showRenewalsIncludesCustomers, customerAboNumberTitle]);

  const getTabCount = useCallback(
    (tabId: renewalsReportTabs): number => {
      switch (tabId) {
        case renewalsReportTabs.IN_RENEWAL_PERIOD:
          return renewalsActionReportData.inRenewal?.length ?? 0;
        case renewalsReportTabs.IN_GRACE_PERIOD:
          return renewalsActionReportData.gracePeriod?.length ?? 0;
        case renewalsReportTabs.EARLY_RENEWAL_PERIOD:
          return renewalsActionReportData.earlyRenewal?.length ?? 0;
        case renewalsReportTabs.ON_AUTO_RENEWAL:
          return renewalsActionReportData.autoRenewal?.length ?? 0;
        case renewalsReportTabs.NON_AUTO_RENEWAL:
          return renewalsActionReportData.nonAutoRenewal?.length ?? 0;
        default:
          return 0;
      }
    },
    [renewalsActionReportData],
  );

  const tabViews = useMemo(
    () => [
      {
        id: renewalsReportTabs.IN_RENEWAL_PERIOD,
        title: `${t("tabs.inRenewalPeriod", defaultContent.tabs.inRenewalPeriod)} (${getTabCount(renewalsReportTabs.IN_RENEWAL_PERIOD)})`,
      },
      {
        id: renewalsReportTabs.IN_GRACE_PERIOD,
        title: `${t("tabs.inGracePeriod", defaultContent.tabs.inGracePeriod)} (${getTabCount(renewalsReportTabs.IN_GRACE_PERIOD)})`,
      },
      {
        id: renewalsReportTabs.NON_AUTO_RENEWAL,
        title: `${t("tabs.notOnAuto", defaultContent.tabs.notOnAuto)} (${getTabCount(renewalsReportTabs.NON_AUTO_RENEWAL)})`,
      },
      {
        id: renewalsReportTabs.ON_AUTO_RENEWAL,
        title: `${t("tabs.onAuto", defaultContent.tabs.onAuto)} (${getTabCount(renewalsReportTabs.ON_AUTO_RENEWAL)})`,
      },
      {
        id: renewalsReportTabs.EARLY_RENEWAL_PERIOD,
        title: `${t("tabs.earlyRenewalPeriod", defaultContent.tabs.earlyRenewalPeriod)} (${getTabCount(renewalsReportTabs.EARLY_RENEWAL_PERIOD)})`,
      },
    ],
    [t, getTabCount],
  );

  const tabsView = activeTabs.map((id: string) => ({
    ...tabViews.find((item) => item.id === id),
  })) as TabView[];

  const PeriodTableLoader = ({ count }: { count: number }) => (
    <div className="Renewals__tableWrapper">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index}>
          <section className="Renewals__tableTitle">
            <LeafRectangleLoader isLoading height="1.5rem" width="8rem" margin="0" />
          </section>
          <div className="Renewals__listViewWrapper">
            <ActionReportListViewLoader />
          </div>
        </div>
      ))}
    </div>
  );

  const activeTabData = getActiveTabData();

  useEffect(() => {
    setEnableTreeView(isTreeViewEnabledForTab(activeView) && activeTabData?.length > 0);
  }, [activeTabData, activeView]);

  const exportAffAbos = useMemo(() => {
    return activeTabData.map((record) => ({
      affAbo: `${record.aff}-${record.abo}`,
    }));
  }, [activeTabData]);

  const otherSectionLabel = t(
    "filter.groupLeader.otherSectionLabel",
    actionReportsContent.filter.groupLeader.otherSectionLabel,
    { ns: "actionReports" },
  ).toString();

  const renderLeaderSection = (section: LeaderSection<RenewalABO>, depth: number): React.ReactNode => {
    const label = section.leader?.name ?? otherSectionLabel;
    return (
      <GroupLeaderSection
        key={`${depth}-${section.leader?.aff ?? "other"}-${section.leader?.abo ?? "other"}`}
        label={label}
        count={section.count}
        pinLevel={section.pinLevel}
        disableCollapse={selectedLeader !== null}
        expandKey={selectedLeader}
      >
        {section.records.length > 0 ? arWrapperList(section.records) : null}
        {section.children.map((child) => renderLeaderSection(child, depth + 1))}
      </GroupLeaderSection>
    );
  };

  const renderTabBody = () => {
    // Scoped view
    if (selectedLeader && groupLeaders.length > 0) {
      const scoped = recordsInLeaderSubtree(
        activeTabData,
        groupLeaderTree,
        selectedLeader,
        getSelfRef,
        getFirstGroupLeader,
      );
      // Match on padded aff: leader records can arrive unpadded ("70")
      // while selectedLeader from compute/URL is zero-padded ("070"), so an
      // exact-string compare misses and the scoped header loses name/pin.
      const selectedEntry = groupLeaders.find(
        (l) => padAffSafe(l.aff) === padAffSafe(selectedLeader.aff) && l.abo === selectedLeader.abo,
      );
      if (scoped.length === 0) {
        return (
          <div className="Renewals__noDownlines">
            <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
          </div>
        );
      }
      return (
        <GroupLeaderSection
          label={selectedEntry?.name ?? `${selectedLeader.aff}-${selectedLeader.abo}`}
          count={scoped.length}
          pinLevel={selectedEntry?.pinLevel ?? 0}
          disableCollapse
        >
          {arWrapperList(scoped)}
        </GroupLeaderSection>
      );
    }

    // "All" view with leaders
    if (groupLeaders.length > 0) {
      const sections = buildLeaderSections(activeTabData, groupLeaderTree, getFirstGroupLeader, getSelfRef);
      return <>{sections.map((s) => renderLeaderSection(s, 0))}</>;
    }

    // Non-JP: flat list as today.
    return arWrapperList(activeTabData);
  };

  const pageTitle = getCanonicalTitle({ id: "renewals" }, t, {});
  const pageDescriptionKey = getPageDescriptionKey(configuration.actionReports.enableEarlyRenewalPeriod);

  return (
    <main className="RenewalsAR">
      <ActionReportHeader
        title={pageTitle}
        description={t(`title.${pageDescriptionKey}`, defaultContent.title[pageDescriptionKey]).toString()}
        reportLength={activeTabData.length}
      />

      <div className="RenewalsAR__header">
        <ActionReportToolbar
          reportLength={activeTabData.length}
          isAllEnriched={true}
          treeViewToggle={{
            enabled: !configuration.actionReports.disableTreeView,
            isTreeView: isTreeView,
            onToggle: handleToggle,
            disabled: !enableTreeView,
            label: t("losTreeView", defaultContent.losTreeView).toString(),
          }}
          kpiSelector={{
            availableKpis: uiKpis.renewalsV2,
            defaultKpis: DEFAULT_KPIS,
            saveViewName: "renewalsKPIs",
            saveViewDescription: "Renewals KPI Preferences",
            maxSelectionsAllowed: maxUiKpis,
            textOverrides: {
              renewalDate: tableColumnContent["renewalDate"],
              annualPPV: tableColumnContent["annualPPV"],
            },
            disabled: isLoading || searchPool.length === 0,
            onOpenTrack: renewalsCustomizeKpiTrack,
            onApplyTrack: renewalsApplyCustomizeKpiTrack,
            onCancelTrack: renewalsCancelCustomizeKpiTrack,
          }}
          search={{
            enabled: true,
            searchCallback: handleSearchCallback,
            selectionCallback: handleSelectionCallback,
            searchText: searchText,
            clearFunction: handleClearSearch,
            disabled: isLoading || searchPool.length === 0,
            onFocus: renewalsSearchTrack,
          }}
          export={{
            moduleId: MODULE_RENEWALS,
            exportAffAbos: exportAffAbos,
            selectedKpis: selectedKpis,
            confirmationMessage: t("export.description", defaultContent.export.description).toString(),
            exportType: exportType,
            startedByLabels: startedByLabels,
            fieldTitleOverrides: fieldTitleOverrides,
          }}
          exportTrack={renewalsExportTrack}
          groupLeaderFilterSlot={
            enableGroupLeaderFilter ? (
              isLoading ? (
                <GroupLeaderFilter leaders={[]} tree={[]} isLoading />
              ) : groupLeaders.length > 0 ? (
                <GroupLeaderFilterErrorBoundary>
                  <GroupLeaderFilter
                    leaders={groupLeaders}
                    tree={groupLeaderTree}
                    allOptionLabel={t("filter.groupLeader.allOption", defaultContent.filter.groupLeader.allOption, {
                      ns: "actionReportsRenewals",
                    }).toString()}
                    onSelectTrack={renewalsSelectGroupLeaderTrack}
                  />
                </GroupLeaderFilterErrorBoundary>
              ) : undefined
            ) : undefined
          }
        />
        <div className={`RenewalsAR__tabs${groupLeaders.length > 0 ? " RenewalsAR__tabs--grouped" : ""}`}>
          <LeafTabViews
            setViewOnClick={setViewOnClick}
            Views={tabsView}
            activeView={activeView}
            buttonify={true}
            isLoading={isLoading}
          />
        </div>
      </div>
      <div className="Renewals__content">
        {isTreeView ? (
          <ActionReportsTreeView path={"renewal"} />
        ) : isLoading ? (
          <div className="Renewals__loading">
            <PeriodTableLoader count={1} />
          </div>
        ) : errorStatus ? (
          <>
            <ErrorTile
              errorStatus={errorStatus}
              isLoading={isLoading}
              clickFunction={refetch}
              tealiumTrackId="Renewals_Action-Report_Error"
            />
          </>
        ) : (
          renderTabBody()
        )}
      </div>
      <div>
        <BonusInfoFooter />
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_RENEWALS}
        notifyDownloadLinkOnClick={() => dispatchOpenExportV2ModalEvent(MODULE_RENEWALS, true)}
      />
    </main>
  );
}
